import { Component, OnDestroy, OnInit } from "@angular/core";
import { ToastService } from "../../../shared/toast.service";
import { AdminPanelCsvService } from "../../../shared/services/admin-panel-csv-service";
import { ExportSurveyDto } from "../../../shared/Models/write/ExportSurveyDto";
import { BuildingService } from "../../building-list/services/building.service";
import { Subject } from "rxjs";
import { first } from "rxjs/operators";
import { Area } from "../../../shared/Models/read/Area";
import { Router } from "@angular/router";
import { PermissionsEnum } from "src/app/shared/Models/enums/PermissionsEnum";
import { PermissionsService } from "src/app/shared/services/permissions.service";
import { AreaService } from "src/app/shared/services/area.service";

@Component({
  selector: "app-admin-panel-csv",
  templateUrl: "./admin-panel-csv.component.html",
  styleUrls: ["./admin-panel-csv.component.scss"],
  providers: [ToastService],
})
export class AdminPanelCsvComponent implements OnInit, OnDestroy {
  isCitySelected: boolean = false;
  isFiberHoodSelected: boolean = false;
  isBlockSelected: boolean = false;

  cities: string[] = [];
  fiberHoodList: Area[] = [];
  blockList: Area[] = [];

  selectedCity: string;
  selectedFiberHood: Area;
  selectedBlock: Area;

  constructor(
    private _router: Router,
    private _buildingService: BuildingService,
    private _areaService: AreaService,
    private _adminPanelCsvService: AdminPanelCsvService,
    private _permissionService: PermissionsService,
    private _toastService: ToastService
  ) {}
  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
  ngOnInit() {
    this.getCityList();
  }

  citySelected(city: string) {
    if (city === this.selectedCity) return;

    this.selectedFiberHood = null;
    this.selectedBlock = null;
    this.selectedCity = city;
    this.blockList = null;

    this.getFiberHoodList();
  }

  fiberHoodSelected(fiberHood: Area) {
    if (fiberHood.id === this.selectedFiberHood?.id) return;

    this.selectedBlock = null;
    this.selectedFiberHood = fiberHood;

    this.getBlockList(this.selectedFiberHood.id);
  }

  blockSelected(block: Area) {
    if (this.selectedBlock !== null && block.id === this.selectedBlock.id)
      return;

    this.selectedBlock = block;
  }

  // retrieves city from areas
  getCityList() {
    this._areaService
      .getCityNames()
      .pipe(first())
      .subscribe(
        (res: string[]) => {
          this.cities = res;
        },
        (err) => {
          this._toastService.showErrorToast(
            $localize`:@@general.error:Something went wrong`,
            $localize`:@@validation.survey.geterror:Something went wrong while retrieving the data.`
          );
        }
      );
  }

  // retrieves fiberhoods
  getFiberHoodList() {
    this._areaService
      .getFiberHoodsForCityName(this.selectedCity)
      .pipe(first())
      .subscribe(
        (res: Area[]) => {
          this.fiberHoodList = res;
        },
        (err) => {
          this._toastService.showErrorToast(
            $localize`:@@general.error:Something went wrong`,
            $localize`:@@validation.survey.geterror:Something went wrong while retrieving the data.`
          );
        }
      );
  }

  // retrieves blockList
  getBlockList(parentId: string) {
    this._areaService
      .getBlocksForFiberHoodId(parentId)
      .pipe(first())
      .subscribe(
        (res: Area[]) => {
          this.blockList = res;
        },
        (err) => {
          this._toastService.showErrorToast(
            $localize`:@@general.error:Something went wrong`,
            $localize`:@@validation.survey.geterror:Something went wrong while retrieving the data.`
          );
        }
      );
  }

  sendAreaIdForCsvTrigger() {
    this._adminPanelCsvService
      .triggerCsv(
        new ExportSurveyDto(
          this.selectedFiberHood.id,
          this.selectedBlock?.id,
          false
        )
      )
      .pipe(first())
      .subscribe(
        (res) => {
          this.downloadFile(res);
          this._toastService.showSuccesToast(
            $localize`:@@admin.csv.triggered:csv has been triggered`,
            $localize`:@@general.csv:csv`
          );
        },
        (err) => {
          this._toastService.showErrorToast(
            $localize`:@@validation.somethingwhentwrong:Something went wrong!`,
            $localize`:@@admin.csv.somethingswrongelaborated:Something went wrong while generating the csv!`
          );
        }
      );
  }

  sendAreaIdForCsvTriggerWithFilteredColumns() {
    this._adminPanelCsvService
      .triggerCsv(
        new ExportSurveyDto(
          this.selectedFiberHood.id,
          this.selectedBlock?.id,
          true
        )
      )
      .pipe(first())
      .subscribe(
        (res) => {
          this.downloadFile(res);
          this._toastService.showSuccesToast(
            $localize`:@@admin.csv.triggered:csv has been triggered`,
            $localize`:@@general.csv:csv`
          );
        },
        (err) => {
          this._toastService.showErrorToast(
            $localize`:@@validation.somethingwhentwrong:Something went wrong!`,
            $localize`:@@admin.csv.somethingswrongelaborated:Something went wrong while generating the csv!`
          );
        }
      );
  }

  downloadFile(response: {
    fileName: string;
    fileType: string;
    data: ArrayBuffer;
  }) {
    let blob = new Blob([response.data], { type: response.fileType });

    const data = window.URL.createObjectURL(blob);
    var link = document.createElement("a");
    link.href = data;
    link.download = response.fileName;
    link.click();

    setTimeout(function () {
      // for Firefox it is necessary to delay revoking the ObjectURL
      window.URL.revokeObjectURL(data);
    }, 100);
  }

  openExportFieldsConfig() {
    if (this.canConfigureExportFields())
      this._router.navigate(["/admin/admin-panel-csv-export-fields"]);
  }

  canConfigureExportFields(): boolean {
    return this._permissionService.allowed(
      PermissionsEnum.ExportFieldsConfiguration
    );
  }
}
